import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Users, UserPlus, Loader2, AlertCircle, Key, Trash2, Eye, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { safeGet, safePost, safeDelete, setAuthToken, getAuthToken } from '../lib/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminClubDetails() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  
  // Reset password dialog state
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(null); // tracks which user email is being sent
  
  // Delete user dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Delete club dialog state
  const [deleteClubDialogOpen, setDeleteClubDialogOpen] = useState(false);
  const [deletingClub, setDeletingClub] = useState(false);
  
  // Add coach developer dialog state
  const [addCoachDevDialogOpen, setAddCoachDevDialogOpen] = useState(false);
  const [newCoachDevEmail, setNewCoachDevEmail] = useState('');
  const [newCoachDevName, setNewCoachDevName] = useState('');
  const [addingCoachDev, setAddingCoachDev] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load organization details
      const orgsResult = await safeGet(`${API_URL}/api/admin/organizations`);
      if (orgsResult.ok) {
        const org = orgsResult.data?.find(o => o.org_id === orgId);
        setOrganization(org);
      }
      
      // Load users
      const usersResult = await safeGet(`${API_URL}/api/admin/organizations/${orgId}/users`);
      if (usersResult.ok) {
        setUsers(usersResult.data || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [orgId]);

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      toast.error('Please enter a new password');
      return;
    }
    
    setResettingPassword(true);
    
    try {
      const result = await safePost(`${API_URL}/api/admin/users/${selectedUser.user_id}/reset-password`, {
        new_password: newPassword
      });
      
      if (!result.ok) {
        throw new Error(result.error || 'Failed to reset password');
      }
      
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSendResetEmail = async (userEmail) => {
    setSendingResetEmail(userEmail);
    
    try {
      const result = await safePost(`${API_URL}/api/admin/users/send-reset-email`, {
        email: userEmail
      });
      
      if (!result.ok) {
        throw new Error(result.data?.detail || result.error || 'Failed to send reset email');
      }
      
      toast.success(`Password reset email sent to ${userEmail}`, {
        description: 'Link valid for 24 hours'
      });
    } catch (err) {
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setSendingResetEmail(null);
    }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    
    try {
      const result = await safeDelete(`${API_URL}/api/admin/users/${userToDelete.user_id}`);
      
      if (!result.ok) {
        throw new Error(result.error || 'Failed to delete user');
      }
      
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClub = async () => {
    setDeletingClub(true);
    try {
      const result = await safeDelete(`${API_URL}/api/admin/organizations/${orgId}`);
      
      if (!result.ok) {
        throw new Error(result.data?.detail || result.error || 'Failed to delete club');
      }
      
      toast.success('Club deleted successfully');
      navigate('/admin');
    } catch (err) {
      toast.error(err.message || 'Failed to delete club');
    } finally {
      setDeletingClub(false);
      setDeleteClubDialogOpen(false);
    }
  };

  const handleAddCoachDeveloper = async () => {
    if (!newCoachDevEmail || !newCoachDevName) {
      toast.error('Please enter both name and email');
      return;
    }
    
    setAddingCoachDev(true);
    try {
      const result = await safePost(`${API_URL}/api/admin/organizations/${orgId}/add-coach-developer`, {
        email: newCoachDevEmail,
        name: newCoachDevName
      });
      
      if (!result.ok) {
        throw new Error(result.data?.detail || result.error || 'Failed to add coach developer');
      }
      
      toast.success('Coach Developer added successfully');
      setAddCoachDevDialogOpen(false);
      setNewCoachDevEmail('');
      setNewCoachDevName('');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to add coach developer');
    } finally {
      setAddingCoachDev(false);
    }
  };

  const handleImpersonate = async (user) => {
    try {
      // Save the admin's current token before impersonating
      const adminToken = getAuthToken();
      console.log('Impersonate: Current token before save:', adminToken?.substring(0, 20) + '...');
      
      if (adminToken) {
        localStorage.setItem('admin_token_backup', adminToken);
        console.log('Impersonate: Saved admin token backup');
      }
      
      const result = await safePost(`${API_URL}/api/admin/impersonate/${user.user_id}`);
      
      if (!result.ok) {
        throw new Error(result.data?.detail || result.error || 'Failed to impersonate user');
      }
      
      console.log('Impersonate: Got new token:', result.data.token?.substring(0, 20) + '...');
      
      // Set the impersonation token as the new auth token
      if (result.data.token) {
        setAuthToken(result.data.token);
      }
      
      // Store impersonation metadata in localStorage for UI purposes
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('impersonated_user', JSON.stringify(result.data.user));
      localStorage.setItem('impersonated_by', result.data.impersonated_by);
      
      toast.success(`Now viewing as ${result.data.user.name}`);
      
      // Navigate based on user role
      // For coaches, go to coach dashboard; for coach developers, go to home
      if (result.data.user.role === 'coach') {
        window.location.href = '/coach';
      } else {
        window.location.href = '/';
      }
    } catch (err) {
      toast.error(err.message || 'Failed to impersonate user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">{error || 'Organization not found'}</p>
            <Button onClick={() => navigate('/admin')}>Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={organization.club_logo} />
              <AvatarFallback className="bg-slate-700 text-slate-300">
                {organization.club_name?.charAt(0) || 'C'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl font-bold font-['Manrope']">{organization.club_name || 'Club Details'}</h1>
              <p className="text-sm text-slate-400">{orgId}</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setAddCoachDevDialogOpen(true)}
              className="bg-transparent border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="add-coach-dev-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Add Coach Developer
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setDeleteClubDialogOpen(true)}
              className="bg-transparent border-red-600 text-red-400 hover:text-white hover:bg-red-600"
              data-testid="delete-club-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Club
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="text-2xl font-bold">{organization.user_count || 0}</p>
                  <p className="text-sm text-slate-500">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{organization.coach_count || 0}</p>
                  <p className="text-sm text-slate-500">Coaches</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-['Manrope']">Users</CardTitle>
                <CardDescription>All users in this organization</CardDescription>
              </div>
              <Button onClick={() => navigate('/admin/users/new')} size="sm">
                <UserPlus className="w-4 h-4 mr-1" />
                Add User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No users in this organization.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div 
                    key={user.user_id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-slate-50 rounded-lg"
                    data-testid={`user-item-${user.user_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="w-10 h-10 shrink-0">
                        <AvatarFallback className="bg-slate-200 text-slate-600">
                          {user.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h3 className="font-medium text-slate-900 truncate">{user.name}</h3>
                        <p className="text-sm text-slate-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap pl-13 sm:pl-0">
                      <Badge variant={user.role === 'coach_developer' ? 'default' : 'secondary'}>
                        {user.role?.replace('_', ' ')}
                      </Badge>
                      
                      {/* Impersonate Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleImpersonate(user)}
                        title="View as this user"
                        data-testid={`impersonate-${user.user_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      {/* Send Password Reset Email Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendResetEmail(user.email)}
                        disabled={sendingResetEmail === user.email}
                        title="Send password reset email"
                        data-testid={`send-reset-email-${user.user_id}`}
                      >
                        {sendingResetEmail === user.email ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                      </Button>
                      
                      {/* Reset Password Button (Set directly) */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          setResetPasswordOpen(true);
                        }}
                        title="Set new password directly"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      
                      {/* Delete Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setUserToDelete(user);
                          setDeleteDialogOpen(true);
                        }}
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter a new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={resettingPassword}>
              {resettingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser} 
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Club Dialog */}
      <Dialog open={deleteClubDialogOpen} onOpenChange={setDeleteClubDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Club</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{organization?.club_name}</strong>? 
              This will also delete all users and coaches in this club. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteClubDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteClub} 
              disabled={deletingClub}
              data-testid="confirm-delete-club-btn"
            >
              {deletingClub && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Club
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Coach Developer Dialog */}
      <Dialog open={addCoachDevDialogOpen} onOpenChange={setAddCoachDevDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Coach Developer</DialogTitle>
            <DialogDescription>
              Add a new coach developer to {organization?.club_name}. They will receive an invite email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="coach-dev-name">Name</Label>
              <Input
                id="coach-dev-name"
                value={newCoachDevName}
                onChange={(e) => setNewCoachDevName(e.target.value)}
                placeholder="Enter name"
                data-testid="coach-dev-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coach-dev-email">Email</Label>
              <Input
                id="coach-dev-email"
                type="email"
                value={newCoachDevEmail}
                onChange={(e) => setNewCoachDevEmail(e.target.value)}
                placeholder="Enter email address"
                data-testid="coach-dev-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCoachDevDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCoachDeveloper} 
              disabled={addingCoachDev || !newCoachDevEmail || !newCoachDevName}
              data-testid="confirm-add-coach-dev-btn"
            >
              {addingCoachDev && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Coach Developer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
